import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('HxcStagingService');

const baseUrl = environment.services.account.baseUrl;
const accountBasePath = environment.services.account.basePath;
const hxcStagingUrl = `${baseUrl}${accountBasePath}/hxc-staging`;
const hxcOnboardingUrl = `${hxcStagingUrl}/onboarding`;

/**
 * Represents a user that has been staged for HXC Partner Creation
 */
export interface StagedUser {
  id: string;
  accountName: string;
  sfdcAccountId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Represents a user in the HXC Onboarding stage
 */
export interface OnboardingUser {
  id: number;
  userId: number;
  username: string;
  fullname: string;
  onboardDate: Date | null;
  emailSentDate: Date | null;
}

/**
 * Response structure for the HXC Staging endpoint
 */
export interface HxcStagingResponse {
  count: number;
  users: StagedUser[];
}

/**
 * Response structure for the HXC Onboarding endpoint
 */
export interface HxcOnboardingResponse {
  count: number;
  users: OnboardingUser[];
}

/**
 * Represents a user that has been successfully processed from Staging to Onboarding
 */
export interface ProcessedUser {
  accountName: string;
  accountId: number;
  sfdcAccountId: string;
  newAccount: boolean;
  userId: number;
  userEmail: string;
  newUser: boolean;
}

/**
 * Warning entry for a staged user found in a different account
 */
export interface CrossAccountWarning {
  existingAccountId: number;
  existingAccountName: string;
}

/**
 * Represents a user that was skipped during processing
 */
export interface SkippedUser {
  accountName: string;
  requestedSfdcAccountId: string;
  existingSfdcAccountId: string;
  existingAccountId: number;
  userEmail: string;
  reason: string;
}

/**
 * Response structure for processing staged users
 */
export interface ProcessStagingResponse {
  processedUsers: ProcessedUser[];
  skippedUsers: SkippedUser[];
}

/**
 * Request parameters for processing staged users
 */
export interface ProcessStagingRequest {
  users: number[];  // Required array of user IDs to process
  onboardDate?: string;  // Optional UTC timestamp in "yyyy-MM-dd'T'HH:mm:ss'Z'" format (ISO 8601)
}

/**
 * Request body for updating an onboarding entry
 */
export interface UpdateOnboardingRequest {
  onboardDate: string;  // Date string in ISO format "yyyy-MM-dd'T'HH:mm:ssZ"
}

/**
 * Represents a user that has been successfully onboarded
 */
export interface OnboardedUser {
  id: number;
  username: string;
  fullname: string;
  onboardDate: Date | null;
  emailSentDate: Date | null;
}

/**
 * Response structure for processing onboarding
 */
export interface OnboardResponse {
  onboardedUsers: OnboardedUser[];
}

/**
 * Service for handling HXC Partner Creation - Staging operations
 */
class HxcStagingService {
  /**
   * Retrieves all users currently staged for HXC Partner Creation
   * @returns Promise containing count and array of staged users
   */
  async getStagedUsers(): Promise<HxcStagingResponse> {
    try {
      log.log('Fetching staged users from:', hxcStagingUrl);
      
      const response = await apiService.makeRequest('get', hxcStagingUrl);
      const responseData = response.data || response;
      
      // Validate response structure
      if (!responseData || typeof responseData !== 'object') {
        log.error('Invalid response format:', responseData);
        throw new Error('Invalid API response format');
      }
      
      // Extract count and users array
      const count = responseData.count || 0;
      const users = responseData.users || [];
      
      // Validate users array
      if (!Array.isArray(users)) {
        log.error('Users property is not an array:', users);
        throw new Error('Invalid users data format');
      }
      
      // Transform and validate each user object
      const stagedUsers: StagedUser[] = users.map((user: any, index: number) => {
        // Validate required fields
        if (!user.id || !user.accountName || !user.sfdcAccountId || !user.email) {
          log.warn(`Staged user at index ${index} missing required fields:`, user);
        }
        
        return {
          id: user.id || '',
          accountName: user.accountName || '',
          sfdcAccountId: user.sfdcAccountId || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.userEmail || ''
        };
      });
      
      log.log(`Successfully fetched ${count} staged users`);
      
      return {
        count,
        users: stagedUsers
      };
    } catch (error) {
      log.error('Error fetching staged users:', error);
      throw error;
    }
  }

  /**
   * Retrieves a single staged user by ID
   * @param id - The unique identifier of the staged user
   * @returns Promise containing the staged user
   */
  async getStagedUser(id: string): Promise<StagedUser> {
    try {
      log.log(`Fetching staged user with ID: ${id}`);
      
      const response = await apiService.makeRequest('get', `${hxcStagingUrl}/${id}`);
      const userData = response.data || response;
      
      // Validate required fields
      if (!userData.id || !userData.accountName || !userData.sfdcAccountId || !userData.email) {
        log.warn('Staged user missing required fields:', userData);
      }
      
      const stagedUser: StagedUser = {
        id: userData.id || '',
        accountName: userData.accountName || '',
        sfdcAccountId: userData.sfdcAccountId || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.userEmail || ''
      };
      
      log.log(`Successfully fetched staged user: ${stagedUser.email}`);
      return stagedUser;
    } catch (error) {
      log.error(`Error fetching staged user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Adds a new user to the staging table
   * @param user - The user data to stage (without id)
   * @returns Promise containing the created staged user with id
   */
  async addStagedUser(user: Omit<StagedUser, 'id'>): Promise<StagedUser> {
    try {
      log.log('Adding new staged user:', user.email);
      
      // Prepare the request body
      const requestBody = {
        accountName: user.accountName,
        sfdcAccountId: user.sfdcAccountId,
        firstName: user.firstName,
        lastName: user.lastName,
        userEmail: user.email.toLowerCase()
      };
      
      const response = await apiService.makeRequest('post', `${hxcStagingUrl}/add`, requestBody);
      const userData = response.data || response;
      
      // Invalidate cache for staged users list
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging`);
      
      const stagedUser: StagedUser = {
        id: userData.id || '',
        accountName: userData.accountName || '',
        sfdcAccountId: userData.sfdcAccountId || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.userEmail || ''
      };
      
      log.log(`Successfully added staged user with ID: ${stagedUser.id}`);
      return stagedUser;
    } catch (error) {
      log.error('Error adding staged user:', error);
      throw error;
    }
  }

  /**
   * Updates an existing staged user
   * @param user - The staged user with updated data (id cannot be changed)
   * @returns Promise containing the updated staged user
   */
  async updateStagedUser(user: StagedUser): Promise<StagedUser> {
    try {
      log.log(`Updating staged user with ID: ${user.id}`);
      
      // Prepare the request body (excluding id as it cannot be changed)
      const requestBody = {
        accountName: user.accountName,
        sfdcAccountId: user.sfdcAccountId,
        firstName: user.firstName,
        lastName: user.lastName,
        userEmail: user.email.toLowerCase()
      };
      
      const response = await apiService.makeRequest('put', `${hxcStagingUrl}/${user.id}`, requestBody);
      const userData = response.data || response;
      
      // Invalidate cache for staged users list and this specific user
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging`);
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/${user.id}`);
      
      const updatedUser: StagedUser = {
        id: userData.id || user.id,
        accountName: userData.accountName || '',
        sfdcAccountId: userData.sfdcAccountId || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || ''
      };
      
      log.log(`Successfully updated staged user: ${updatedUser.email}`);
      return updatedUser;
    } catch (error) {
      log.error(`Error updating staged user with ID ${user.id}:`, error);
      throw error;
    }
  }

  /**
   * Deletes a staged user by ID
   * @param id - The unique identifier of the staged user to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteStagedUser(id: string): Promise<void> {
    try {
      log.log(`Deleting staged user with ID: ${id}`);
      
      await apiService.makeRequest('delete', `${hxcStagingUrl}/${id}`);
      
      // Invalidate cache for staged users list and this specific user
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging`);
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/${id}`);
      
      log.log(`Successfully deleted staged user with ID: ${id}`);
    } catch (error) {
      log.error(`Error deleting staged user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Processes staged users from Staging to Onboarding
   * This endpoint performs extensive validation and may create accounts and users
   * @param userIds - Array of user IDs to process (required to prevent accidental mass processing)
   * @param onboardDate - Optional UTC timestamp in "yyyy-MM-dd'T'HH:mm:ss'Z'" format (ISO 8601)
   * @returns Promise containing lists of successfully processed and skipped users
   */
  async processStagedUsers(
    userIds: number[],
    onboardDate?: string,
    allowMigrate?: boolean
  ): Promise<ProcessStagingResponse> {
    try {
      // Validate that userIds array is provided and not empty
      if (!userIds || userIds.length === 0) {
        log.error('No user IDs provided for processing');
        throw new Error('User IDs array is required and cannot be empty');
      }

      log.log(`Processing ${userIds.length} staged users. OnboardDate: ${onboardDate || 'none'}. AllowMigrate: ${allowMigrate || false}`);

      // Prepare request body - always include users array
      const requestBody: ProcessStagingRequest = {
        users: userIds
      };

      if (onboardDate) {
        requestBody.onboardDate = onboardDate;
      }

      log.log('Processing request body:', requestBody);

      const url = allowMigrate ? `${hxcStagingUrl}?allowMigrate=true` : hxcStagingUrl;
      const response = await apiService.makeRequest('post', url, requestBody);
      const responseData = response.data || response;
      
      // Validate response structure
      if (!responseData || typeof responseData !== 'object') {
        log.error('Invalid response format:', responseData);
        throw new Error('Invalid API response format');
      }
      
      // Extract processed and skipped users
      const processedUsers = responseData.processedUsers || [];
      const skippedUsers = responseData.skippedUsers || [];
      
      // Validate arrays
      if (!Array.isArray(processedUsers) || !Array.isArray(skippedUsers)) {
        log.error('Response arrays are invalid:', { processedUsers, skippedUsers });
        throw new Error('Invalid response array format');
      }
      
      // Transform processed users
      const transformedProcessed: ProcessedUser[] = processedUsers.map((user: any) => ({
        accountName: user.accountName || '',
        accountId: user.accountId || 0,
        sfdcAccountId: user.sfdcAccountId || '',
        newAccount: user.newAccount === true,
        userId: user.userId || 0,
        userEmail: user.userEmail || '',
        newUser: user.newUser === true
      }));
      
      // Transform skipped users
      const transformedSkipped: SkippedUser[] = skippedUsers.map((user: any) => ({
        accountName: user.accountName || '',
        requestedSfdcAccountId: user.requestedSfdcAccountId || '',
        existingSfdcAccountId: user.existingSfdcAccountId || '',
        existingAccountId: user.existingAccountId || 0,
        userEmail: user.userEmail || '',
        reason: user.reason || 'Unknown error'
      }));
      
      // Invalidate cache for staged users (they've been moved)
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging`);
      
      log.log(`Processing complete. Processed: ${transformedProcessed.length}, Skipped: ${transformedSkipped.length}`);
      
      if (transformedSkipped.length > 0) {
        log.warn('Some users were skipped during processing:', transformedSkipped);
      }
      
      return {
        processedUsers: transformedProcessed,
        skippedUsers: transformedSkipped
      };
    } catch (error) {
      log.error('Error processing staged users:', error);
      throw error;
    }
  }

  async getOnboardingUsers(): Promise<HxcOnboardingResponse> {
    try {
      log.log('Fetching onboarding users from:', hxcOnboardingUrl);
      
      const response = await apiService.makeRequest('get', hxcOnboardingUrl);
      const responseData = response.data || response;
      
      // Validate response structure
      if (!responseData || typeof responseData !== 'object') {
        log.error('Invalid response format:', responseData);
        throw new Error('Invalid API response format');
      }
      
      // Extract count and users array
      const count = responseData.count || 0;
      const users = responseData.users || [];
      
      // Validate users array
      if (!Array.isArray(users)) {
        log.error('Users property is not an array:', users);
        throw new Error('Invalid users data format');
      }
      
      // Transform and validate each user object
      const onboardingUsers: OnboardingUser[] = users.map((user: any, index: number) => {
        // Validate required fields
        if (!user.id || !user.userId) {
          log.warn(`Onboarding user at index ${index} missing required fields:`, user);
        }
        
        return {
          id: user.id || 0,
          userId: user.userId || 0,
          username: user.username || '',
          fullname: user.fullname || '',
          onboardDate: user.onboardDate ? new Date(user.onboardDate) : null,
          emailSentDate: user.emailSentDate ? new Date(user.emailSentDate) : null
        };
      });
      
      log.log(`Successfully fetched ${count} onboarding users`);
      
      return {
        count,
        users: onboardingUsers
      };
    } catch (error) {
      log.error('Error fetching onboarding users:', error);
      throw error;
    }
  }

  /**
   * Retrieves a single onboarding user by ID
   * @param id - The unique identifier of the onboarding entry
   * @returns Promise containing the onboarding user
   */
  async getOnboardingUser(id: number): Promise<OnboardingUser> {
    try {
      log.log(`Fetching onboarding user with ID: ${id}`);
      
      const response = await apiService.makeRequest('get', `${hxcOnboardingUrl}/${id}`);
      const userData = response.data || response;
      
      // Validate required fields
      if (!userData.id || !userData.userId) {
        log.warn('Onboarding user missing required fields:', userData);
      }
      
      const onboardingUser: OnboardingUser = {
        id: userData.id || 0,
        userId: userData.userId || 0,
        username: userData.username || '',
        fullname: userData.fullname || '',
        onboardDate: userData.onboardDate ? new Date(userData.onboardDate) : null,
        emailSentDate: userData.emailSentDate ? new Date(userData.emailSentDate) : null
      };
      
      log.log(`Successfully fetched onboarding user: ${onboardingUser.username}`);
      return onboardingUser;
    } catch (error) {
      log.error(`Error fetching onboarding user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Updates an onboarding entry (only onboardDate can be modified)
   * @param id - The unique identifier of the onboarding entry
   * @param onboardDate - The new onboard date in ISO format (yyyy-MM-dd'T'HH:mm:ssZ)
   * @returns Promise containing the updated onboarding user
   */
  async updateOnboardingUser(id: number, onboardDate: string): Promise<OnboardingUser> {
    try {
      log.log(`Updating onboarding user with ID: ${id}`);
      
      // Validate date format (basic check)
      if (!onboardDate || onboardDate.trim() === '') {
        log.error('Invalid onboardDate provided');
        throw new Error('onboardDate is required');
      }
      
      const requestBody: UpdateOnboardingRequest = {
        onboardDate: onboardDate
      };
      
      const response = await apiService.makeRequest('put', `${hxcOnboardingUrl}/${id}`, requestBody);
      const userData = response.data || response;
      
      // Invalidate cache for onboarding users list and this specific user
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/onboarding`);
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/onboarding/${id}`);
      
      const updatedUser: OnboardingUser = {
        id: userData.id || id,
        userId: userData.userId || 0,
        username: userData.username || '',
        fullname: userData.fullname || '',
        onboardDate: userData.onboardDate ? new Date(userData.onboardDate) : null,
        emailSentDate: userData.emailSentDate ? new Date(userData.emailSentDate) : null
      };
      
      log.log(`Successfully updated onboarding user: ${updatedUser.username}`);
      return updatedUser;
    } catch (error) {
      log.error(`Error updating onboarding user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Deletes an onboarding entry by ID
   * @param id - The unique identifier of the onboarding entry to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteOnboardingUser(id: number): Promise<void> {
    try {
      log.log(`Deleting onboarding user with ID: ${id}`);
      
      await apiService.makeRequest('delete', `${hxcOnboardingUrl}/${id}`);
      
      // Invalidate cache for onboarding users list and this specific user
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/onboarding`);
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/onboarding/${id}`);
      
      log.log(`Successfully deleted onboarding user with ID: ${id}`);
    } catch (error) {
      log.error(`Error deleting onboarding user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Processes onboarding users - sends welcome emails and removes from onboarding list
   * Only processes users with onboardDate in the past or present
   * @returns Promise containing list of successfully onboarded users
   */
  async processOnboarding(): Promise<OnboardResponse> {
    try {
      log.log('Processing onboarding users with onboardDate in the past');
      
      const response = await apiService.makeRequest('post', hxcOnboardingUrl);
      const responseData = response.data || response;
      
      // Validate response structure
      if (!responseData || typeof responseData !== 'object') {
        log.error('Invalid response format:', responseData);
        throw new Error('Invalid API response format');
      }
      
      // Extract onboarded users array
      const onboardedUsers = responseData.onboardedUsers || [];
      
      // Validate array
      if (!Array.isArray(onboardedUsers)) {
        log.error('onboardedUsers property is not an array:', onboardedUsers);
        throw new Error('Invalid onboardedUsers data format');
      }
      
      // Transform onboarded users
      const transformedUsers: OnboardedUser[] = onboardedUsers.map((user: any) => ({
        id: user.id || 0,
        username: user.username || '',
        fullname: user.fullname || '',
        onboardDate: user.onboardDate ? new Date(user.onboardDate) : null,
        emailSentDate: user.emailSentDate ? new Date(user.emailSentDate) : null
      }));
      
      // Invalidate cache for onboarding users list (users were removed after processing)
      apiService.invalidateCacheByPattern(`${accountBasePath}/hxc-staging/onboarding`);
      
      log.log(`Successfully processed ${transformedUsers.length} onboarding users`);
      
      return {
        onboardedUsers: transformedUsers
      };
    } catch (error) {
      log.error('Error processing onboarding users:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const hxcStagingService = new HxcStagingService();
export default hxcStagingService;
